import { useEffect } from 'react';
import { loadUserSettings, saveUserSettings, debouncedSave, SETTING_KEYS } from '../utils/userSettings';
import {
  persistTrackModeToLocalStorage,
  markTrackModeAppliedLocal,
  buildDashboardAutosavePayload,
  prepareDashboardSettingsHydration,
  shouldAllowDashboardAutosave,
  markDashboardSettingsHydrated
} from '../utils/dashboard/dashboardSettingsHelpers';
import { PLAYBACK_STEP_MS } from '../utils/dashboard/constants';
import { migrateLegacyMissions } from '../utils/dashboard/missionsModel';
import { loadCommandMissionState, BOARD_POLL_MS } from '../utils/dashboard/missionPersist';

/**
 * 指挥页用户设置：首次水合 + 变更自动保存
 * 任务看板（missions/groups）走指挥台共享 loadCommandMissionState，不按登录账号隔离
 */
export default function useDashboardSettingsLifecycle({
  settingsLoaded,
  state,
  setters,
  // fetchDevices 保留入参兼容；实际拉取由 useDashboardDeviceRefreshEffects 统一调度
  fetchDevices: _fetchDevices,
  onShowLeftPanel
}) {
  const {
    setStartTime,
    setEndTime,
    setHideInvalid,
    setShowOnlineOnly,
    setTrackMode,
    setTrackMinutes,
    setTrackHours,
    setTrackDays,
    setPlaybackRange,
    setSidebarWidth,
    setCollapsed,
    setShowDownload,
    setShowTracks,
    setAllTracksVisible,
    setTrackColors,
    setShowMemberNames,
    setRefreshInterval,
    setTrackModeApplied,
    setSettingsLoaded,
    setMissionMemberIds,
    setMissionGroups,
    setMissionMode,
    setMemberPanelTab,
    setMissions,
    setActiveMissionId
  } = setters || {};

  const applySharedMissionBoard = (shared, tabFallback) => {
    if (!shared) return;
    try {
      const migrated = migrateLegacyMissions({
        missions: shared.missions || shared[SETTING_KEYS.MISSIONS] || [],
        activeMissionId: shared.activeMissionId || shared[SETTING_KEYS.ACTIVE_MISSION_ID] || null,
        missionMemberIds: shared.missionMemberIds || shared[SETTING_KEYS.MISSION_MEMBER_IDS] || [],
        missionGroups: shared.missionGroups || shared[SETTING_KEYS.MISSION_GROUPS] || [],
        missionMode: shared.missionMode ?? shared[SETTING_KEYS.MISSION_MODE]
      });
      setMissions?.(migrated.missions);
      setActiveMissionId?.(migrated.activeMissionId);
      const active = (migrated.missions || []).find((m) => m.id === migrated.activeMissionId)
        || migrated.missions[0]
        || null;
      setMissionMemberIds?.(active ? (active.memberIds || []) : []);
      setMissionGroups?.(active ? (active.groups || []) : []);
      setMissionMode?.(Boolean(migrated.missions.length > 0 && (active?.memberIds || []).length > 0));
      if (tabFallback) setMemberPanelTab?.(tabFallback);
    } catch (err) {
      console.warn('[mission] apply shared board failed:', err?.message || err);
    }
  };

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await loadUserSettings();
        const { state: hydrated, fixed, fixSavePayload, trackModeConfig } =
          prepareDashboardSettingsHydration(settings, SETTING_KEYS);

        setStartTime?.(hydrated.startTime);
        setEndTime?.(hydrated.endTime);
        setHideInvalid?.(hydrated.hideInvalid);
        setShowOnlineOnly?.(hydrated.showOnlineOnly);
        setTrackMode?.(hydrated.trackMode);
        setTrackMinutes?.(hydrated.trackMinutes);
        setTrackHours?.(hydrated.trackHours);
        setTrackDays?.(hydrated.trackDays);
        setPlaybackRange?.(hydrated.playbackRange);
        setSidebarWidth?.(hydrated.sidebarWidth);
        setCollapsed?.(hydrated.collapsed);
        setShowDownload?.(hydrated.showDownload);
        setShowTracks?.(hydrated.showTracks);
        setAllTracksVisible?.(hydrated.allTracksVisible);
        setTrackColors?.(hydrated.trackColors);
        setShowMemberNames?.(hydrated.showMemberNames);
        setRefreshInterval?.(hydrated.refreshInterval);

        // 任务看板：共享表优先；个人 settings 仅作空板兜底并回灌
        let missionSource = hydrated;
        try {
          const shared = await loadCommandMissionState({
            [SETTING_KEYS.MISSION_MEMBER_IDS]: hydrated.missionMemberIds,
            [SETTING_KEYS.MISSION_GROUPS]: hydrated.missionGroups,
            [SETTING_KEYS.MISSION_MODE]: hydrated.missionMode,
            [SETTING_KEYS.MISSIONS]: hydrated.missions,
            [SETTING_KEYS.ACTIVE_MISSION_ID]: hydrated.activeMissionId,
            [SETTING_KEYS.MEMBER_PANEL_TAB]: hydrated.memberPanelTab
          });
          if (shared) {
            missionSource = {
              ...hydrated,
              missionMemberIds: shared[SETTING_KEYS.MISSION_MEMBER_IDS] || [],
              missionGroups: shared[SETTING_KEYS.MISSION_GROUPS] || [],
              missionMode: Boolean(shared[SETTING_KEYS.MISSION_MODE]),
              missions: shared[SETTING_KEYS.MISSIONS] || [],
              activeMissionId: shared[SETTING_KEYS.ACTIVE_MISSION_ID] || null
            };
          }
        } catch (shareErr) {
          console.warn('[mission] load shared board failed, use personal:', shareErr?.message || shareErr);
        }

        try {
          const migrated = migrateLegacyMissions({
            missions: missionSource.missions,
            activeMissionId: missionSource.activeMissionId,
            missionMemberIds: missionSource.missionMemberIds,
            missionGroups: missionSource.missionGroups,
            missionMode: missionSource.missionMode
          });
          setMissions?.(migrated.missions);
          setActiveMissionId?.(migrated.activeMissionId);
          const active = (migrated.missions || []).find((m) => m.id === migrated.activeMissionId)
            || migrated.missions[0]
            || null;
          setMissionMemberIds?.(active ? (active.memberIds || []) : []);
          setMissionGroups?.(active ? (active.groups || []) : []);
          setMissionMode?.(Boolean(migrated.missions.length > 0 && (active?.memberIds || []).length > 0));
        } catch (migErr) {
          console.warn('[mission] migrate hydrate failed, legacy path:', migErr?.message || migErr);
          setMissionMemberIds?.(missionSource.missionMemberIds || []);
          setMissionGroups?.(Array.isArray(missionSource.missionGroups) ? missionSource.missionGroups : []);
          setMissionMode?.(Boolean(missionSource.missionMode) && (missionSource.missionMemberIds || []).length > 0);
        }
        if (hydrated.memberPanelTab) setMemberPanelTab?.(hydrated.memberPanelTab);
        console.log('加载用户设置的刷新间隔:', hydrated.refreshInterval, 'ms');

        persistTrackModeToLocalStorage(
          hydrated.trackMode,
          hydrated.trackMinutes,
          hydrated.trackHours,
          hydrated.trackDays
        );

        if (fixed && fixSavePayload) {
          console.log('检测到数据库轨迹模式数据异常，修复为recent_days模式');
          saveUserSettings(fixSavePayload);
        }

        setTrackModeApplied?.(true);
        setSettingsLoaded?.(true);
        markTrackModeAppliedLocal(
          hydrated.trackMode,
          hydrated.trackMinutes,
          hydrated.trackHours,
          hydrated.trackDays
        );
        console.log('轨迹模式已从用户设置恢复并自动应用', trackModeConfig);

        markDashboardSettingsHydrated(PLAYBACK_STEP_MS);
        console.log('用户设置加载完成:', settings);

        setTimeout(() => {
          onShowLeftPanel?.();
          console.log('左侧面板已自动显示');
        }, 2000);
      } catch (e) {
        console.error('加载用户设置失败:', e);
        setSettingsLoaded?.(true);
        setTrackModeApplied?.(true);
        if (typeof window !== 'undefined') {
          window.dashboardSettingsHydrated = true;
        }
      }
    };

    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 项 6：共享看板短轮询（WS 推送为主，轮询兜底）
  useEffect(() => {
    if (!settingsLoaded) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const shared = await loadCommandMissionState(null);
        if (cancelled || !shared) return;
        applySharedMissionBoard(shared, null);
      } catch (err) {
        console.warn('[mission] board poll failed:', err?.message || err);
      }
    };
    const t = setInterval(tick, BOARD_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsLoaded]);

  useEffect(() => {
    if (!shouldAllowDashboardAutosave({ settingsLoaded })) return;

    // 任务看板由 persistMissionState → 共享 API 负责；个人 autosave 只保留 UI 偏好（含 memberPanelTab）
    const currentSettings = buildDashboardAutosavePayload(state || {}, SETTING_KEYS);
    persistTrackModeToLocalStorage(
      state?.trackMode,
      state?.trackMinutes,
      state?.trackHours,
      state?.trackDays
    );

    console.log('自动保存设置:', currentSettings);
    debouncedSave(currentSettings, 2000);
  }, [
    settingsLoaded,
    state?.startTime,
    state?.endTime,
    state?.hideInvalid,
    state?.showOnlineOnly,
    state?.trackMode,
    state?.trackMinutes,
    state?.trackHours,
    state?.trackDays,
    state?.sidebarWidth,
    state?.collapsed,
    state?.showDownload,
    state?.showTracks,
    state?.allTracksVisible,
    state?.trackColors,
    state?.refreshInterval,
    state?.showMemberNames,
    state?.memberPanelTab
    // mission* / missions / activeMissionId：不进个人 autosave，避免按账号覆盖共享看板
    // playbackRange 不加入依赖，避免无限循环；保存时仍写入当前 playbackRange
  ]);
}
